import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import MailIcon from '@material-ui/icons/Mail';
import PhoneAndroidIcon from '@material-ui/icons/PhoneAndroid';
import SendIcon from '@material-ui/icons/Send';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ContactService from './Service/contactService';
import Header from './Header';
import ModernFooter from './ModernFooter';
import './styles/bootstrap.min.css';
import './styles/main.css';

const useStyles = makeStyles((theme) => ({
    contactSectionWrapper: {
        background: 'linear-gradient(180deg, #f8fbfe 0%, #e8f4fc 50%, #f8fbfe 100%)',
        paddingTop: 100,
        paddingBottom: theme.spacing(8),
        minHeight: 'calc(100vh - 200px)',
        [theme.breakpoints.down('sm')]: {
            paddingTop: 88,
            paddingBottom: theme.spacing(5),
        },
    },
    contactInfoCard: {
        background: 'white',
        borderRadius: 20,
        padding: theme.spacing(4),
        height: '100%',
        boxShadow: '0 10px 40px rgba(25, 118, 210, 0.08), 0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid rgba(25, 118, 210, 0.06)',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        '&:hover': {
            boxShadow: '0 16px 48px rgba(25, 118, 210, 0.12), 0 4px 12px rgba(0,0,0,0.06)',
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(3),
            borderRadius: 16,
        },
    },
    contactIconCircle: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        marginRight: theme.spacing(2),
        flexShrink: 0,
    },
    contactFormCard: {
        background: 'white',
        borderRadius: 20,
        padding: theme.spacing(4),
        boxShadow: '0 10px 40px rgba(25, 118, 210, 0.08), 0 2px 8px rgba(0,0,0,0.04)',
        border: '1px solid rgba(25, 118, 210, 0.06)',
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#fafbfc',
            '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
            '&:hover fieldset': { borderColor: '#1976d2', borderWidth: '1px' },
            '&.Mui-focused fieldset': { borderWidth: '2px', borderColor: '#1976d2' },
        },
        '& .MuiOutlinedInput-input': {
            padding: '14px 16px',
            fontSize: 15,
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(3),
            borderRadius: 16,
        },
    },
    contactSubmitBtn: {
        padding: '14px 28px',
        borderRadius: 12,
        fontSize: 15,
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)',
        '&:hover': {
            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.45)',
        },
    },
}));

export default function ContactUs(props) {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [contactSubject, setContactSubject] = useState('');
    const [contactMessage, setContactMessage] = useState('');
    const [contactSubmitting, setContactSubmitting] = useState(false);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [contactDialogMessage, setContactDialogMessage] = useState('');
    const [contactDialogType, setContactDialogType] = useState('success');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (contactSubmitting) return;
        if (!contactName || !contactEmail || !contactMessage) {
            setContactDialogType('error');
            setContactDialogMessage('Please fill in your name, email, and message.');
            setContactDialogOpen(true);
            return;
        }
        setContactSubmitting(true);
        try {
            await ContactService.submitContact({
                name: contactName,
                email: contactEmail,
                phone: contactPhone,
                subject: contactSubject,
                message: contactMessage,
            });
            setContactDialogType('success');
            setContactDialogMessage('Thank you for contacting us. We will get back to you soon.');
            setContactDialogOpen(true);
            setContactName('');
            setContactEmail('');
            setContactPhone('');
            setContactSubject('');
            setContactMessage('');
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.message ||
                'Failed to submit your message. Please try again.';
            console.error('[ContactUs] Error:', err);
            setContactDialogType('error');
            setContactDialogMessage(msg);
            setContactDialogOpen(true);
        } finally {
            setContactSubmitting(false);
        }
    };

    return (
        <>
            <Header />
            <Box className={classes.contactSectionWrapper}>
                <Container style={{ maxWidth: 960 }}>
                    <Typography
                        variant="h3"
                        align="center"
                        style={{
                            color: '#0d47a1',
                            fontWeight: 700,
                            marginBottom: 12,
                            fontSize: isMobile ? 26 : 34,
                            letterSpacing: '-0.02em',
                            fontFamily: '"DM Sans", sans-serif',
                        }}
                    >
                        Contact Us
                    </Typography>
                    <Typography
                        variant="body1"
                        align="center"
                        style={{
                            color: '#5f6368',
                            fontSize: isMobile ? 15 : 17,
                            maxWidth: 520,
                            margin: '0 auto ' + (isMobile ? 32 : 48) + 'px',
                            lineHeight: 1.6,
                        }}
                    >
                        Have questions or need support? Send us a message and our team will get back to you shortly.
                    </Typography>
                    <Grid container spacing={isMobile ? 3 : 4} alignItems="stretch">
                        <Grid item xs={12} md={5}>
                            <Box className={classes.contactInfoCard}>
                                <Typography
                                    variant="h6"
                                    style={{
                                        fontFamily: '"DM Sans", sans-serif',
                                        fontWeight: 700,
                                        color: '#1a237e',
                                        marginBottom: 8,
                                        fontSize: 18,
                                    }}
                                >
                                    Get in touch
                                </Typography>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: '#5f6368',
                                        marginBottom: 28,
                                        lineHeight: 1.65,
                                        fontSize: 14,
                                    }}
                                >
                                    Reach out for partnerships, support, or general inquiries. We're here to help you make ticketing seamless.
                                </Typography>
                                <Box style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 20 }}>
                                    <Box className={classes.contactIconCircle}>
                                        <LocationOnIcon style={{ fontSize: 22 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" style={{ color: '#5f6368', display: 'block', marginBottom: 2 }}>Address</Typography>
                                        <Typography variant="body2" style={{ color: '#202124', fontWeight: 500 }}>
                                            Hyderabad, Telangana, India
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 20 }}>
                                    <Box className={classes.contactIconCircle}>
                                        <MailIcon style={{ fontSize: 22 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" style={{ color: '#5f6368', display: 'block', marginBottom: 2 }}>Email</Typography>
                                        <Typography variant="body2" style={{ color: '#202124', fontWeight: 500 }}>
                                        amar@srshta.com
                                        </Typography>
                                    </Box>
                                </Box>
                                {/* <Box style={{ display: 'flex', alignItems: 'flex-start' }}>
                                    <Box className={classes.contactIconCircle}>
                                        <PhoneAndroidIcon style={{ fontSize: 22 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" style={{ color: '#5f6368', display: 'block', marginBottom: 2 }}>Phone</Typography>
                                        <Typography variant="body2" style={{ color: '#202124', fontWeight: 500 }}>
                                            +91-99999-99999
                                        </Typography>
                                    </Box>
                                </Box> */}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box component="form" noValidate className={classes.contactFormCard} onSubmit={handleSubmit}>
                                <Typography
                                    variant="subtitle1"
                                    style={{
                                        fontWeight: 600,
                                        color: '#1a237e',
                                        marginBottom: 20,
                                        fontSize: 16,
                                    }}
                                >
                                    Send a message
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name"
                                            variant="outlined"
                                            value={contactName}
                                            onChange={(e) => setContactName(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            variant="outlined"
                                            type="email"
                                            value={contactEmail}
                                            onChange={(e) => setContactEmail(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone (optional)"
                                            variant="outlined"
                                            value={contactPhone}
                                            onChange={(e) => setContactPhone(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Subject (optional)"
                                            variant="outlined"
                                            value={contactSubject}
                                            onChange={(e) => setContactSubject(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Message"
                                            variant="outlined"
                                            multiline
                                            rows={4}
                                            value={contactMessage}
                                            onChange={(e) => setContactMessage(e.target.value)}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{ marginTop: 8 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            disabled={contactSubmitting}
                                            className={classes.contactSubmitBtn}
                                            endIcon={<SendIcon style={{ fontSize: 20 }} />}
                                        >
                                            {contactSubmitting ? 'Sending...' : 'Send Message'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Dialog
                open={contactDialogOpen}
                onClose={() => setContactDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                aria-labelledby="contact-dialog-title"
            >
                <DialogTitle id="contact-dialog-title" disableTypography>
                    <Box display="flex" alignItems="center">
                        <Box
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 12,
                                backgroundColor:
                                    contactDialogType === 'success' ? 'rgba(46, 125, 50, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                            }}
                        >
                            <CheckCircleIcon
                                style={{
                                    color: contactDialogType === 'success' ? '#2e7d32' : '#d32f2f',
                                    fontSize: 24,
                                }}
                            />
                        </Box>
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {contactDialogType === 'success' ? 'Message sent' : 'Something went wrong'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" style={{ color: '#5f6368', lineHeight: 1.6 }}>
                        {contactDialogMessage}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setContactDialogOpen(false)}
                        color="primary"
                        variant="contained"
                        style={{ borderRadius: 10, textTransform: 'none', padding: '6px 20px', fontWeight: 600 }}
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <ModernFooter history={props.history} isMobile={isMobile} />
        </>
    );
}
